/**
 * Schémas Zod — API keys (gestion des clés MCP / intégrations).
 *
 * NOTE — Bundles MCP :
 * Les bundles `mcp:read` et `mcp:write` ne sont PAS des valeurs de l'enum
 * `app.api_key_scope` côté DB. Ce sont des raccourcis applicatifs qu'on
 * expand côté API au moment de la création de la clé vers les scopes
 * granulaires DB réels (`projects:read`, `extractions:write`, etc.).
 *
 * Cf. `apps/api/src/routes/api-keys.ts` pour le mapping.
 */

import { z } from "zod";

import type { API_KEY_SCOPES } from "../constants.js";

import { PaginationSchema, UuidSchema } from "./common.js";

// ---------------------------------------------------------------------------
// Bundles applicatifs (haut niveau, exposés au client)
// ---------------------------------------------------------------------------

export const API_KEY_BUNDLES = [
  "mcp:read",
  "mcp:write",
  "mcp:insurers",
  // Chunk 9.Z.2 — bundle dédié rétention (décision PO option 1).
  "mcp:retention",
  "mcp:projects",
] as const;
export type ApiKeyBundle = (typeof API_KEY_BUNDLES)[number];

export const ApiKeyBundleSchema = z.enum(API_KEY_BUNDLES);

/**
 * Mapping bundle → scopes granulaires DB (enum `app.api_key_scope`).
 *
 * `mcp:read`     : tout ce qui est lecture sur les ressources métier MCP exposera.
 * `mcp:write`    : `mcp:read` + écritures permises (création/résolution d'anomalies,
 *                  override de fields). Pas d'`admin:write` — les api_keys ne peuvent
 *                  pas créer d'utilisateurs ou de tenants.
 * `mcp:projects` : lecture/écriture des dossiers, attribution explicite. Permet
 *                  la commission MCP sans élargir les clés mcp:write existantes.
 *                  Ce profil autorise les routes projects:write, pas seulement
 *                  la commission ; il ne donne aucun droit d’envoi ou d’administration.
 * `mcp:retention`: bundle dédié 9.Z.2 — `retention:read` SEUL. Rend utilisables les
 *                  10 outils MCP de la famille rétention (registre de classification,
 *                  politiques de rétention, blocages légaux, journal d'audit de
 *                  rétention), qui renvoyaient un 403 à toute clé provisionnée par
 *                  bundle. **Lecture seule et rien d'autre** : `retention:write`
 *                  n'existe pas dans l'enum, donc aucune écriture n'est accordable —
 *                  les routes POST/PATCH de ces routeurs exigent `retention:write`
 *                  et refuseront toujours une api_key. Volontairement PAS ajouté à
 *                  `mcp:read`/`mcp:write` : il s'agit de donnée de conformité et de
 *                  gouvernance légale, pas de métier courtage, donc l'attribution
 *                  reste explicite et au cas par cas (parité `mcp:insurers`).
 * `mcp:insurers` : bundle dédié 9.V.2c-2 — `insurers:read` + `insurers:write`.
 *                  Délivre l'écriture assureur à une api_key SANS élargir `mcp:write`
 *                  (qui reste lecture-seule sur les assureurs). Portée réelle : le
 *                  carnet de contacts/emails (routes nested `insurers:write`,
 *                  api_key autorisée, matrice 9.V.2a). Les mutations STRUCTURELLES
 *                  d'assureur (create/patch/activate/deactivate) restent user-only
 *                  (`INSURERS_FORBIDDEN_USER_ONLY`) quel que soit le scope porté.
 */
export const API_KEY_BUNDLE_TO_SCOPES: Record<
  ApiKeyBundle,
  readonly (typeof API_KEY_SCOPES)[number][]
> = {
  "mcp:read": [
    "projects:read",
    "companies:read",
    "submissions:read",
    "extractions:read",
    "reports:read",
    // 9.V.1 — lecture du référentiel assureurs exposée aux clients MCP.
    // `insurers:write` reste hors bundle (écritures structurelles admin-only).
    "insurers:read",
  ],
  "mcp:write": [
    "projects:read",
    "companies:read",
    "submissions:read",
    "submissions:write",
    "extractions:read",
    "extractions:write",
    "reports:read",
    "reports:write",
    // 9.V.1 — lecture seule ; l'écriture assureur passe par le bundle dédié
    // `mcp:insurers` (jamais élargir `mcp:write` — décision PO 9.V.2c-2).
    "insurers:read",
  ],
  // 9.V.2c-2 (E) — bundle dédié écriture assureur (carnet contacts/emails).
  "mcp:insurers": ["insurers:read", "insurers:write"],
  // 9.Z.2 — bundle dédié rétention. UN SEUL scope, strictement celui exigé par les
  // 10 outils read-only de la famille. Aucun scope d'écriture ni d'administration :
  // les ajouter serait accorder implicitement une capacité que la décision PO exclut.
  "mcp:retention": ["retention:read"],
  "mcp:projects": ["projects:read", "projects:write"],
};

// ---------------------------------------------------------------------------
// CREATE
// ---------------------------------------------------------------------------

export const CreateApiKeySchema = z.object({
  name: z.string().trim().min(1).max(120),
  /** Bundle MCP de haut niveau. Expansé en scopes granulaires côté serveur. */
  bundle: ApiKeyBundleSchema,
  /** Optionnel : durée de vie en jours (1-365). Si omis : pas d'expiration. */
  expires_in_days: z.number().int().min(1).max(365).optional(),
  /**
   * Tenant cible. Optionnel : si super_admin sans header `x-tenant-id`,
   * ce champ permet de scoper la clé à un tenant explicite. Pour un
   * tenant_admin, ce champ est ignoré (toujours forcé à son propre tenant).
   */
  tenant_id: UuidSchema.optional(),
  /**
   * 9.M.7 — Optionnel : lier la clé à une `app.agent_identities` (D30
   * forensic). Si fourni, la clé est associée à un agent IA / système
   * externe — toutes les writes via cette clé peupleront `*_by_agent` côté
   * tables métier + `audit_log.agent_id`. Le handler valide que l'agent
   * existe et que son scope tenant est compatible (global ou même tenant).
   *
   * Phase 1 : assignation **uniquement à la création**. Pas de PATCH
   * d'assignation post-create (dette D30.2 — UI complète api_keys mgmt).
   */
  agent_id: UuidSchema.nullable().optional(),
});
export type CreateApiKeyInput = z.infer<typeof CreateApiKeySchema>;

// ---------------------------------------------------------------------------
// FILTER (listing)
// ---------------------------------------------------------------------------

export const ApiKeyFilterSchema = z.object({
  /** Inclure les clés révoquées (`revoked_at IS NOT NULL`). */
  include_revoked: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((v) => (typeof v === "string" ? v === "true" : Boolean(v))),
  /** Recherche substring (ILIKE) sur `name`. */
  q: z.string().trim().min(1).max(120).optional(),
});
export type ApiKeyFilter = z.infer<typeof ApiKeyFilterSchema>;

export const ApiKeyListQuerySchema = PaginationSchema.merge(ApiKeyFilterSchema);

// ---------------------------------------------------------------------------
// REVOKE
// ---------------------------------------------------------------------------

export const RevokeApiKeyParamsSchema = z.object({
  id: UuidSchema,
});
